// src/i18n/en/views/dominioPropio.js
//
// Guía que ve el anfitrión para apuntar su dominio a la web del constructor.
// Base en inglés del fichero; el original con las notas largas está en es/.
//
// Marcadores que rellena la pantalla:
//   {dominio} {destino} {nombre} {tipo} {valor} {encontrado} {actual} {sitio}
//   {panel} {panelCorto}

export const dominioPropio = {
  cabecera: {
    titulo: "Connect your own domain",
    subtitulo:
      "Your site is currently at {actual}. When you are done it will be at your usual address.",
    duracion: "Ten minutes of your time. The internet does the rest on its own.",
  },

  pasos: {
    uno: "Your domain",
    dos: "The two records in {panelCorto}",
    tres: "Check",
    unoDetalle: "You type the address you want to use",
    dosDetalle: "You copy two lines into your domain panel",
    tresDetalle: "We check that everything is in place",
    indicador: "Step {paso} of 3",
  },

  // ── Paso 1: escribir el dominio ────────────────────────────────────────────
  paso1: {
    titulo: "Which address do you want for your site?",
    descripcion:
      "Type the domain you already own. We do not sell domains: this connects one that is already yours.",
    etiqueta: "Your domain",
    marcador: "mydomain.com",
    ayuda: "Type it as it is, with no “https://” and no trailing slashes.",

    formaTitulo: "And how should it read?",
    formaRaiz: {
      titulo: "mydomain.com",
      descripcion: "The short address, with nothing in front. This is the usual one.",
    },
    formaWww: {
      titulo: "www.mydomain.com",
      descripcion: "The classic one, with “www” in front.",
    },
    formaSubdominio: {
      titulo: "bookings.mydomain.com",
      descripcion:
        "A separate address, so you do not touch the site you already have at {dominio}.",
    },
    subdominioEtiqueta: "The word that goes in front",
    subdominioMarcador: "bookings",
    subdominioAyuda: "Just the word. We add the rest.",

    avisoWebExistente: {
      titulo: "Do you already have a site at that address?",
      texto:
        "If {dominio} shows another site today, it will stop showing and this one will take its place. If you would rather keep it, use a separate address such as bookings.{dominio}.",
    },

    boton: "Continue",
    botonComprobando: "Checking the domain…",
  },

  // ── Dónde tiene el dominio ─────────────────────────────────────────────────
  proveedor: {
    pregunta: "Where did you buy or manage {dominio}?",
    ayuda:
      "It is the company you pay the renewal to every year. If you cannot remember, look for the last domain invoice in your email.",
    cloudflare: "Cloudflare",
    cloudflareDetalle: "Full instructions, screen by screen",
    otro: "Another company",
    otroDetalle: "GoDaddy, IONOS, Namecheap, Dinahosting…",
    otroTexto:
      "The records are exactly the same. Only the position of the button changes. Look in your panel for something that says “DNS”, “DNS Zone” or “DNS Management” and copy the lines from the table above there, one by one.",
    noLoSe: "I do not know",
    noLoSeTexto:
      "Write to us with your domain and we will tell you where it is and what needs changing.",
  },

  // ── Paso 2: los registros ──────────────────────────────────────────────────
  paso2: {
    titulo: "Now you have to tell your domain where your site is",
    descripcion:
      "Your domain is a name. On its own it leads nowhere: someone has to write in a notebook that this name belongs to our server. That notebook is called DNS and it lives in your domain panel.",

    queEsUnRegistro: {
      titulo: "What exactly you are going to do",
      texto:
        "You are going to add one line to that notebook. Just one. It has three slots: what it is called, where it points and whether it goes through a filter. Nothing else.",
      textoOtro:
        "You are going to add lines to that notebook. Each one has two slots: what it is called and where it points. Nothing else.",
    },

    copiaTitulo: "Copy this data exactly as it is",
    copiaAyuda:
      "Use the copy buttons. One extra space or one wrong letter and it will not work.",

    tabla: {
      tipo: "Type",
      nombre: "Name",
      destino: "Target",
      contenido: "Content",
      proxy: "Proxy status",
      ttl: "TTL",
      valorProxy: "DNS only (grey cloud)",
      valorTtl: "Auto",
    },

    copiar: "Copy",
    copiado: "Copied",
    copiarTodo: "Copy the data",

    tipos: {
      cnameTitulo: "CNAME",
      cnameTexto:
        "It is a forward: it says that your domain is served at {destino}. If we ever change servers, you will not have to touch anything.",
      txtTitulo: "TXT",
      txtTexto:
        "It is a signed note. It leads nowhere: it only proves the domain is yours. You can delete it once the site works, but it is easier to leave it in place.",
    },

    nombreTitulo: "What to write in “Name”",
    nombreRaiz:
      "For the short domain ({dominio}) write an at sign: @. In the DNS notebook the at sign means “the bare domain, with nothing in front”. Do not write the whole {dominio}.",
    nombreWww: "For www.{dominio} write only: www",
    nombreSubdominio:
      "For {nombre}.{dominio} write only the word in front: {nombre}",
    nombreAviso:
      "In Cloudflare you write only that part. It adds “.{dominio}” by itself and shows it to you under the field.",
    nombreAvisoOtro:
      "In almost every panel you write only that part and they add “.{dominio}”. If yours asks for the whole name, write {nombre}.{dominio}.",
    nombreAvisoOtroRaiz:
      "If your panel does not accept “@”, leave the name empty: in many of them it means the same thing, the domain with nothing in front.",

    volver: "I have done it, back to WACheckin",
    ayudaEnviar: "Email me this data",
    ayudaEnviarTexto:
      "If your IT person or whoever built your site is going to do it, we will send the instructions to your email so you can forward them.",
  },

  // ── Cloudflare, paso a paso ────────────────────────────────────────────────
  cloudflare: {
    titulo: "Do it in Cloudflare",
    subtitulo: "Ten minutes. Have your Cloudflare email and password to hand.",

    pasos: {
      uno: "Sign in at dash.cloudflare.com with your account.",
      dos: "The first screen lists your domains. Click {dominio}.",
      tres: "In the left menu open “DNS” and then “Records”.",
      cuatro:
        "You will see a list with the lines you already have. Click the “Add record” button.",
      cinco: "In “Type” pick {tipo} from the dropdown.",
      seis: "In “Name” write {nombre}",
      siete: "In “Target” paste {valor}",
      ocho:
        "In “Proxy status” click the orange cloud. It has to turn grey and read “DNS only”.",
      nueve: "Leave “TTL” on “Auto”. Do not touch it.",
      diez: "Click “Save”.",
      once:
        "If the table above has more than one line, repeat from step 4 with the next one.",
    },

    hecho: "That is it. Go back to WACheckin and click “Check”.",

    avisoAntes: {
      titulo: "Before you save, read this",
      texto:
        "Cloudflare turns that cloud orange on its own, without asking. If you leave it orange, the connection will not work. Step 8 is the important one on this whole screen.",
    },

    accesoNegado: {
      titulo: "Cannot get into Cloudflare?",
      texto:
        "The account is usually in the name of whoever built your site. Ask them to follow these steps or to add you as a member of the domain.",
    },
  },

  // ── La nube naranja ────────────────────────────────────────────────────────
  proxy: {
    titulo: "The orange cloud: this is what always fails",
    queEs:
      "Next to each DNS line, Cloudflare shows a little cloud. Orange means the traffic goes through Cloudflare first. Grey means it goes straight through.",
    porQueFalla:
      "With the orange cloud, when someone asks for your domain Cloudflare does not answer {destino}: it answers with its own addresses. Our check asks, gets Cloudflare and does not find the record we asked for. Even if you wrote it correctly, for us it is as if it did not exist.",
    porQueFalla2:
      "And there is a second, worse consequence: the security padlock for your site is issued by checking that the domain reaches us. With the orange cloud it does not, so it cannot be issued and the browser will warn that the site is not secure.",
    comoSeArregla:
      "Click the cloud. It turns grey and the text beside it changes to “DNS only”. Save. That is all.",
    grisEsSeguro:
      "Turning it grey does not leave your site unprotected. We serve your site ourselves, with its certificate and behind our own protection. Cloudflare was only in the middle because of how it came set up out of the box.",
    ttlBloqueado:
      "A detail that throws people off: while the cloud is orange, Cloudflare will not let you touch the TTL. Turning it grey unlocks it. Leave it on “Auto”.",
    etiquetaProxied: "Proxied — orange cloud — NO",
    etiquetaSoloDns: "DNS only — grey cloud — YES",
  },

  // ── El dominio corto (raíz) ────────────────────────────────────────────────
  raiz: {
    titulo: "If you use the short domain: there is nothing special to do",
    texto:
      "There is an old internet rule saying that the short domain ({dominio}, with nothing in front) does not accept this kind of record. With most companies that forces you to take detours.",
    flattening:
      "Cloudflare solves it on its own. When the “Name” is @, it applies something of its own called CNAME flattening: it stores your forward and, when someone asks, answers with the final address directly. You do not have to switch anything on, it comes enabled on every plan, the free one included.",
    avisoAjusteGlobal:
      "What you should watch: do not touch the “CNAME flattening for all CNAME records” option under “DNS” → “Settings”. Applied to everything, it stops answering forwards as they are written and breaks the ownership checks of other services you may have: email, Google, your shop…",
    tituloOtro: "If you use the short domain ({dominio})",
    textoOtroA:
      "The domain with nothing in front takes type A lines, the ones in the table. Every company accepts them.",
    textoOtroCname:
      "Many companies do not allow a CNAME on the domain with nothing in front. If yours will not let you save that line, use “www” or a separate address such as bookings.{dominio}, or write to us and we will sort it out with you.",
    conflicto: {
      titulo: "If the short domain already had something on it",
      texto:
        "One name cannot have two targets at once. If the list already has a type A or CNAME line with the “Name” set to @ (or to “www”, if you are going to use www), delete it before adding ours. Cloudflare will warn you if you do not.",
      textoOtro:
        "One name cannot have two targets at once. If your panel already has a type A or CNAME line with that same name, delete it before adding ours.",
      comoBorrar:
        "Hover over that line, click “Edit” and then “Delete”.",
      apunta:
        "Write down on paper what it said before you delete it, in case you need to go back.",
    },
  },

  // ── Cuánto tarda ───────────────────────────────────────────────────────────
  propagacion: {
    titulo: "How long until it shows?",
    texto:
      "Cloudflare publishes the change straight away. What takes time is the rest of the internet finding out: every computer keeps a copy of the notebook for a while so it does not have to ask a thousand times.",
    plazoNormal:
      "With TTL on “Auto”, Cloudflare asks for that copy to last five minutes. In practice it usually works between five and thirty minutes after you save.",
    textoOtro:
      "Your panel publishes the change within a few minutes. What takes time is the rest of the internet finding out: every computer keeps a copy of the notebook for a while so it does not have to ask a thousand times.",
    plazoNormalOtro:
      "It usually works between ten minutes and an hour after you save. Some companies take longer to publish the change: allow up to 24 hours before worrying.",
    plazoMalo:
      "If your domain was already showing another site, your own computer may take longer to let go of what it had stored. It is not a fault. Allow 24 hours before worrying.",
    truco:
      "A trick so you do not go mad checking: open it in a private window, or from your phone on mobile data instead of wifi. That way you see what a new visitor sees, not what your computer has stored.",
    dominioNuevo:
      "One more thing: if you have just added the domain to Cloudflare for the first time, Cloudflare takes up to 24 hours to mark it as active. Until then everything behaves as if the clouds were grey, even if you see them orange. Wait until the domain reads “Active” before assuming anything is wrong.",
    ssl:
      "When the check passes, we issue the security padlock. That takes a few more minutes. Until it is done, the browser may warn that the site is not secure: that is normal and it clears up on its own.",
  },

  // ── Paso 3: comprobar ──────────────────────────────────────────────────────
  paso3: {
    titulo: "Let us check it",
    descripcion:
      "We look at your domain and tell you what we find. If something is missing, we tell you what it is and how to fix it.",
    boton: "Check now",
    botonReintentar: "Check again",
    comprobando: "Looking at your records…",
    comprobandoAyuda: "It takes a few seconds.",

    resultado: {
      registroOk: "The DNS record is in place and points to us",
      registroKo: "We cannot see the record yet",
      proxyOk: "The cloud is grey, just as it should be",
      proxyKo: "The cloud is orange",
      sslOk: "Security padlock issued",
      sslPendiente: "Issuing the security padlock…",
      webOk: "Your site opens at {dominio}, with a padlock",
      webActivando: "We are activating your site on your domain, with its security padlock",
      webDespues: "Then we check that your site opens, with a padlock",
    },

    activando: {
      titulo: "Your domain points correctly. We are activating your site",
      texto:
        "Only the security padlock is left, it is issued automatically and usually takes a minute or two. Do not change anything in {panel}: we are checking, and you will see here when it is done.",
    },

    exito: {
      titulo: "Done, your site is now at {dominio}",
      texto:
        "You can type the address in the browser and see it. The old address {actual} still works, so the links you already shared will not break.",
      verWeb: "View my site",
      soloWww: "Your site opens at the «www» address. If you also want it to work without «www», ask your domain provider for a redirect to the «www» address, or an ALIAS/ANAME record at the root if their panel allows it.",
      copiarEnlace: "Copy the address",
      siguiente: "Now put it on your Booking, Airbnb and Instagram profiles, and in your email signature.",
    },

    pendiente: {
      titulo: "Not there yet, and that is normal",
      texto:
        "If you have just saved it in {panel}, give it a few minutes and click again. You do not need to stay here: we will email you as soon as it is ready.",
      avisarme: "Email me when it is ready",
    },
  },

  // ── Estados del dominio en la ficha ────────────────────────────────────────
  estados: {
    sinConectar: "Not connected",
    pendiente: "Waiting for the records",
    comprobando: "Checking",
    emitiendoSsl: "Issuing the padlock",
    activo: "Working",
    error: "With a fault",
    activoAyuda: "Your site is at {dominio} and the connection is secure.",
    activandoAyuda: "Your domain points correctly. In a few minutes your site will open at {dominio}, with a padlock.",
  },

  // ── Errores ────────────────────────────────────────────────────────────────
  errores: {
    formatoInvalido: {
      titulo: "That does not look like a domain",
      texto: "Write it like this: mydomain.com. No spaces and nothing else.",
    },
    conProtocolo: {
      titulo: "The “https://” is not needed",
      texto: "Remove it and leave just the name: {dominio}",
    },
    conRuta: {
      titulo: "Everything after the slash is not needed",
      texto: "Leave only the first part: {dominio}",
    },
    sinExtension: {
      titulo: "The ending is missing",
      texto: "A domain ends in .com, .es, .net… Check that it did not get cut short.",
    },
    dominioNoExiste: {
      titulo: "That domain does not exist",
      texto:
        "Nobody is registered under that name. Check the spelling. If you have just bought it, wait a few hours and try again.",
    },
    dominioCaducado: {
      titulo: "That domain looks expired",
      texto:
        "Go to the company you bought it from and check that the renewal is paid. While it is expired it cannot be connected.",
    },
    yaUsadoOtraCuenta: {
      titulo: "That domain is already connected to another account",
      texto:
        "A domain can only point to one place at a time. If {dominio} is yours, write to us and we will disconnect it from the other account so you can use it here.",
      contactar: "Write to support",
    },
    yaUsadoEstaCuenta: {
      titulo: "You already have it connected",
      texto:
        "{dominio} is in use on “{sitio}”. Disconnect it there or choose another address for this site.",
    },
    registrosNoAparecen: {
      titulo: "We still cannot see anything on your domain",
      texto:
        "It is usually a matter of minutes: you have just saved it and it has not reached everywhere yet. Wait ten minutes and click “Check” again.",
      repasa: "If half an hour has gone by, check three things in {panel}:",
      repasa1: "That in “Name” you wrote {nombre} and not the whole domain.",
      repasa2: "That you clicked “Save” and the line appears in the list.",
      repasa3: "That you wrote it on {dominio} and not on another domain in your account.",
    },
    proxyActivado: {
      titulo: "The orange cloud is switched on",
      texto:
        "We can see your domain, but it goes through the Cloudflare proxy and that hides the real target from us. While it stays orange, neither the check nor the security padlock can work.",
      solucion:
        "Go to “DNS” → “Records”, find the line named {nombre}, click the orange cloud until it turns grey and reads “DNS only”, and save. Then come back and click “Check”.",
    },
    apuntaOtroSitio: {
      titulo: "Your domain points somewhere else",
      texto:
        "We found {encontrado} and we expected {destino}. Almost always it is an old record from the previous site that stayed behind.",
      solucion:
        "In “DNS” → “Records”, delete the old line named {nombre} and leave only ours.",
    },
    destinoAntiguo: {
      titulo: "The value we gave you before did not work",
      texto:
        "Your record leads to {encontrado}, which is what we told you, but that address does not work. The mistake was ours, not yours.",
      solucion: "In {panel}, edit the {nombre} record and set the value to {destino}. Leave the TXT as it is.",
    },
    registroDuplicado: {
      titulo: "There are two records for the same name",
      texto:
        "One name cannot lead to two places. Delete the one that is not ours and check again.",
    },
    error1014: {
      titulo: "If the browser shows “Error 1014”",
      texto:
        "It is another face of the same old problem: the orange cloud. Turn it grey (“DNS only”) and it goes away.",
    },
    sslFallido: {
      titulo: "We could not issue the security padlock",
      texto:
        "The DNS record is right, but the padlock will not come through. It is usually the orange cloud again, or an old record getting in the way. Try again in a while; if it stays the same, write to us and we will look into it.",
      reintentar: "Retry the issue",
    },
    sinPermisos: {
      titulo: "You cannot change the domain",
      texto: "Ask the account owner to do it.",
    },
    servidor: {
      titulo: "We could not check it right now",
      texto: "Something failed on our side. Try again in a few minutes.",
    },
    guardar: "The domain could not be saved. Try again.",
  },

  // ── Desconectar ────────────────────────────────────────────────────────────
  desconectar: {
    boton: "Disconnect this domain",
    titulo: "Disconnect {dominio}?",
    texto:
      "Your site will stop showing at {dominio} and will go back to showing only at {actual}. Anyone typing your domain will find nothing until you point it somewhere else.",
    aviso: "The records you created in {panel} are still there. Delete them yourself if you no longer want them.",
    cancelar: "Cancel",
    confirmar: "Disconnect",
    hecho: "Domain disconnected.",
  },

  // ── Dudas ──────────────────────────────────────────────────────────────────
  dudas: {
    titulo: "Questions that always come up",

    correoPregunta: "Is this going to break my @{dominio} email?",
    correoTexto:
      "No. Email travels on other lines of the notebook, the MX ones, and we do not touch them. As long as you do not delete those, your email stays the same.",

    otraWebPregunta: "I have another site on that domain. What happens to it?",
    otraWebTexto:
      "It stops showing: the domain switches to this one. If you want to keep both, use a separate address such as bookings.{dominio} for the WACheckin one.",

    volverAtrasPregunta: "Can I undo this?",
    volverAtrasTexto:
      "Yes, whenever you like. You undo it where you did it: delete the record in {panel} and put back the one you had. It takes the same few minutes to show.",

    wwwPregunta: "Will it work with and without “www”?",
    wwwTexto:
      "Yes, if you add both lines we give you. One leads and the other forwards to the first, so Google does not see two identical sites.",

    costePregunta: "Does it cost me anything?",
    costeTexto:
      "Connecting it, no. You keep paying for the domain where you bought it, just as before.",

    quienPregunta: "I do not feel up to doing this.",
    quienTexto:
      "That is fair, it is the driest part of the whole process. Write to us with your domain and we will get it done with you over the phone in ten minutes.",
  },

  // ── Botonera ───────────────────────────────────────────────────────────────
  botones: {
    atras: "Back",
    siguiente: "Next",
    guardar: "Save",
    guardando: "Saving…",
    cerrar: "Close",
    entendido: "Got it",
    abrirCloudflare: "Open Cloudflare",
    soporte: "Get help from WACheckin",
    verGuia: "See the guide again",
  },

  avisos: {
    copiado: "Copied to the clipboard",
    copiadoError: "It could not be copied. Select it by hand.",
    guardado: "Domain saved. Now add the records in {panel}.",
    conectado: "Your site is now at {dominio}!",
    correoEnviado: "We have sent the instructions to your email.",
  },
};
